import React from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';

import DropdownIndicator from './components/DropdownIndicator';
import ClearIndicator from './components/ClearIndicator';
import MultiValueRemove from './components/MultiValueRemove';

const CustomSelect = ({
  options, placeholder, isMulti, isClearable,
  defaultValue, onChange, value, menuPlacement,
  isSearchable, isDisabled, maxSelectedValue,
  infoText, onInfoClick, isLoading, closeMenuOnSelect,
  containerStyle, controlStyle, menuPortal
}) => {
  const customStyles = {
    control: (provided, state) => {
      return {
        ...provided,
        background: state.isFocused ? 'var(--select-bg-focus)' : 'var(--select-bg)',
        border: 'none',
        boxShadow: 'none',
        borderRadius: 7,
        paddingRight: 5,
        ...controlStyle
      };
    },
    valueContainer: (provided) => ({
      ...provided,
      paddingTop: 13,
      paddingBottom: 13,
      paddingLeft: 13,
      textTransform: 'capitalize',
      ...containerStyle
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#A1A5B7'
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none'
    }),
    multiValue: (provided) => ({
      ...provided,
      borderRadius: 5,
      textTransform: 'capitalize',
      color: '#fff'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--select-text)'
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: 0,
      boxShadow: '0px 0px 50px 0px rgba(82, 63, 105, 0.15)',
      background: 'var(--select-menu-bg)',
      zIndex: 2000,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => {
      return {
        ...provided,
        paddingTop: 13,
        paddingBottom: 13,
        borderRadius: 8,
        fontWeight: 300,
      };
    },
    option: (provided, { isFocused, isSelected }) => {
      return {
        ...provided,
        // eslint-disable-next-line no-nested-ternary
        background: isSelected
          ? 'var(--select-option-hover)'
          : (isFocused ? 'var(--select-bg-focus)' : 'var(--select-option-bg)'),
        color: isSelected ? '#0095E8FF' : 'var(--select-text)',
        cursor: 'pointer'
      };
    },
    groupHeading: (provided) => ({
      ...provided,
      color: '#F1416C',
      fontSize: 15
    }),
  };

  return (
    <div className="w-full bg-white dark:bg-background-dark">
      <Select
        isSearchable={isSearchable}
        menuPlacement={menuPlacement}
        menuPortalTarget={menuPortal && typeof document !== 'undefined' ? document.body : undefined}
        menuPosition={menuPortal ? 'fixed' : 'absolute'}
        menuShouldScrollIntoView={!menuPortal}
        value={value}
        isMulti={isMulti}
        defaultValue={defaultValue}
        placeholder={placeholder}
        components={{
          DropdownIndicator, ClearIndicator, MultiValueRemove
        }}
        options={options}
        styles={customStyles}
        isClearable={isClearable}
        onChange={onChange}
        isDisabled={isDisabled}
        isOptionDisabled={() => value?.length >= maxSelectedValue}
        isLoading={isLoading}
        closeMenuOnSelect={closeMenuOnSelect}
      />
      {infoText && (
        <span
          className="text-[11px] cursor-pointer globalLink hover:no-underline hover:text-primary"
          role="presentation"
          onClick={onInfoClick}
        >
          {infoText}
        </span>
      )}
    </div>
  );
};

CustomSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  value: PropTypes.arrayOf(PropTypes.shape({})),
  placeholder: PropTypes.string,
  infoText: PropTypes.string,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  defaultValue: PropTypes.shape({}),
  containerStyle: PropTypes.shape({}),
  controlStyle: PropTypes.shape({}),
  onChange: PropTypes.func,
  menuPlacement: PropTypes.oneOf(['bottom', 'top', 'auto']),
  maxSelectedValue: PropTypes.number,
  onInfoClick: PropTypes.func,
  isLoading: PropTypes.bool,
  menuPortal: PropTypes.bool
};

CustomSelect.defaultProps = {
  placeholder: 'Select option',
  isMulti: false,
  isClearable: false,
  isSearchable: false,
  isDisabled: false,
  closeMenuOnSelect: true,
  defaultValue: undefined,
  value: '',
  onChange: () => {},
  menuPlacement: 'auto',
  maxSelectedValue: 10,
  infoText: null,
  onInfoClick: () => {},
  isLoading: false,
  containerStyle: {},
  controlStyle: {},
  menuPortal: false,
};

export default CustomSelect;
